"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TriangleAlert, Flame, Droplet, Waves, Pipette, ShieldAlert, Gauge, Wrench, CircleQuestionMark as HelpCircle, PhoneCall, ArrowRight, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
interface PlumbingProblem {
  id: string;
  title: string;
  description: string;
  severity: "Emergency" | "Urgent" | "Standard";
  icon: React.ComponentType<{
    className?: string;
  }>;
  destinationUrl: string;
}
const problemsList: PlumbingProblem[] = [{
  id: "water-leak",
  title: "Water Leak",
  description: "Active leaks from dripping pipes, fixture joints, or moisture beneath floors and walls.",
  severity: "Urgent",
  icon: Droplet,
  destinationUrl: "/problems/"
}, {
  id: "toilet-problem",
  title: "Toilet Problem",
  description: "My toilet is overflowing, running continuously, or refusing to flush properly.",
  severity: "Urgent",
  icon: Waves,
  destinationUrl: "/problems/"
}, {
  id: "clogged-drain",
  title: "Clogged Drain",
  description: "Standing water in sinks, bathtubs, or showers that drains sluggishly or not at all.",
  severity: "Standard",
  icon: Pipette,
  destinationUrl: "/problems/"
}, {
  id: "water-heater-problem",
  title: "Water Heater Problem",
  description: "No hot water, pooling water around the tank base, or loud banging sounds.",
  severity: "Urgent",
  icon: Flame,
  destinationUrl: "/problems/"
}, {
  id: "sewer-problem",
  title: "Sewer Problem",
  description: "Foul sewage smells, multiple fixture backups, or waste rising into lower drains.",
  severity: "Emergency",
  icon: ShieldAlert,
  destinationUrl: "/problems/"
}, {
  id: "low-water-pressure",
  title: "Low Water Pressure",
  description: "Sudden or gradual loss of strong water stream across home faucets and shower heads.",
  severity: "Standard",
  icon: Gauge,
  destinationUrl: "/problems/"
}, {
  id: "pipe-problem",
  title: "Pipe Problem",
  description: "Burst, cracked, corroded, or noisy vibrating water supply and drainage lines.",
  severity: "Emergency",
  icon: Wrench,
  destinationUrl: "/problems/"
}, {
  id: "plumbing-emergency",
  title: "Plumbing Emergency",
  description: "Uncontrolled indoor flooding, sudden line ruptures, or critical hazardous leaks.",
  severity: "Emergency",
  icon: TriangleAlert,
  destinationUrl: "/problems/"
}, {
  id: "not-sure",
  title: "Not Sure",
  description: "Unexplained water sounds, damp spots, mystery drops, or unknown plumbing issues.",
  severity: "Urgent",
  icon: HelpCircle,
  destinationUrl: "/problems/"
}];
export default function Happening() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.05
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.35,
        ease: "easeOut"
      }
    }
  };
  return <section id="whats-happening" data-nav="light" className="relative bg-background py-14 md:py-20 lg:py-24 text-foreground border-b border-border">
      <div className="container mx-auto max-w-[1240px] px-5 sm:px-6 md:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mb-10 md:mb-14">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary text-secondary-foreground text-xs font-bold tracking-wide uppercase mb-3">
            <Sparkles className="size-3.5 text-accent" />
            <span>Instant Triage System</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-extrabold tracking-tight text-foreground leading-tight">
            What{"'"}s Happening?
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-default leading-relaxed">
            Select your urgent plumbing issue below for immediate diagnostic guidance, or call our 24/7 Jonesboro dispatch team directly for on-site emergency support.
          </p>
        </div>

        {/* Triage Grid (3 Columns on Desktop) */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {problemsList.map((problem, index) => {
          const IconComponent = problem.icon;
          const isEmergency = problem.severity === "Emergency";
          return <motion.div key={problem.id} data-index={index} variants={cardVariants} className="group flex h-full">
                <Card className="flex flex-col justify-between w-full rounded-2xl border border-border bg-card shadow-xs transition-[border-color,box-shadow,background-color] duration-200 hover:border-primary hover:bg-background hover:shadow-md">
                  <div>
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div className="size-12 rounded-xl bg-secondary flex items-center justify-center text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                          <IconComponent className="size-6" />
                        </div>
                        <span className={`inline-flex items-center text-xs font-bold px-2.5 py-1 rounded-full ${isEmergency ? "bg-destructive/10 text-destructive border border-destructive/20" : "bg-muted text-muted-foreground border border-border"}`}>
                          {problem.severity}
                        </span>
                      </div>
                      <CardTitle className="text-lg sm:text-xl font-bold font-default text-card-foreground group-hover:text-primary transition-colors">
                        {problem.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                        {problem.description}
                      </CardDescription>
                    </CardContent>
                  </div>

                  <div className="px-6 pt-2 pb-6 mt-auto border-t border-border/60">
                    <div className="flex items-center justify-between gap-3 pt-3">
                      <Link to={problem.destinationUrl} className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-primary group-hover:text-foreground transition-colors">
                        <span>Fix This Issue</span>
                        <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-1" />
                      </Link>

                      <Button size="sm" asChild className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90 font-bold text-xs h-8 px-3">
                        <a href="tel:4708479076">
                          <PhoneCall className="size-3.5" />
                          <span>Call 24/7</span>
                        </a>
                      </Button>
                    </div>
                  </div>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Quick Triage Banner */}
        <div className="mt-12 rounded-2xl bg-secondary border border-border p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <div className="size-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0 mt-0.5">
              <HelpCircle className="size-5" />
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-bold font-default text-foreground">
                Don{"'"}t see your exact plumbing problem?
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                Our licensed Jonesboro plumbers handle all emergency leak detections, sewer lines, and fixture installations 24 hours a day.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto shrink-0">
            <Button asChild className="w-full sm:w-auto rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-semibold px-6 shadow-xs">
              <Link to="/contact/">Request Service Form</Link>
            </Button>
            <Button asChild className="w-full sm:w-auto rounded-full bg-accent text-accent-foreground hover:bg-accent/90 font-bold px-6 shadow-xs">
              <a href="tel:4708479076">
                <PhoneCall className="size-4" />
                <span>(470) 847-9076</span>
              </a>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}